'use client';

import { CellContext, HeaderContext } from '@tanstack/react-table';
import { AccountOpenPosition } from 'src/@types/positions';
import { DataTableColumnDef } from 'src/components/primitives/Table/types';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import OpenPositionsTableMarginDisplay from 'src/components/PositionsTable/OpenPositionsTableMarginDisplay';
import {
	MarketCell,
	SizeCell,
	EntryPriceCell,
	MarkPriceCell,
	PnLCell,
	LiqPriceCell,
	TpSlCell,
	ActionsCell,
} from './cells';
import { Trash } from '@velocity-exchange/icons';

export interface OpenPositionsTableMeta {
	onCloseAllPositions: () => void;
}

function getMeta(
	context:
		| CellContext<AccountOpenPosition, unknown>
		| HeaderContext<AccountOpenPosition, unknown>
): OpenPositionsTableMeta {
	return context.table.options.meta as OpenPositionsTableMeta;
}

export function getOpenPositionsColumns(): DataTableColumnDef<AccountOpenPosition>[] {
	return [
		{
			id: 'market',
			accessorKey: 'marketSymbol',
			header: 'Market',
			meta: {
				width: { ratio: 15, minPx: 100 },
				bodyCellTestId: 'position-market',
				bodyCellClassName: 'first:pl-0',
				headerCellClassName: 'first:xl:pl-md',
			},
			cell: ({ row }) => (
				<MarketCell
					marketIndex={row.original.marketIndex}
					marketSymbol={row.original.marketSymbol}
					direction={row.original.direction}
					isIsolated={row.original.isIsolated ?? false}
					hidePositionType={row.original.isIsolatedBalanceOnly}
				/>
			),
		},
		{
			id: 'size',
			accessorKey: 'baseSize',
			header: 'Size',
			meta: {
				width: { ratio: 11, minPx: 100 },
				bodyCellTestId: 'position-size',
			},
			cell: ({ row }) => <SizeCell position={row.original} />,
		},
		{
			id: 'entryPrice',
			accessorKey: 'entryPrice',
			header: 'Entry Price',
			meta: {
				width: { ratio: 11, minPx: 90 },
				bodyCellTestId: 'position-entry-price',
			},
			cell: ({ row }) => <EntryPriceCell position={row.original} />,
		},
		{
			id: 'markPrice',
			meta: { width: { ratio: 11, minPx: 110 } },
			header: () => (
				<DefinitionTooltip
					definitionId="openPositionsMarkPercentChange"
					className="capitalize"
				>
					Mark Price (%)
				</DefinitionTooltip>
			),
			cell: ({ row }) => <MarkPriceCell position={row.original} />,
		},
		{
			id: 'pnl',
			accessorKey: 'pnlVsMark',
			header: 'P&L (ROI%)',
			meta: {
				width: { ratio: 12, minPx: 110 },
				bodyCellTestId: 'position-pnl',
			},
			cell: ({ row }) => <PnLCell position={row.original} />,
		},
		{
			id: 'margin',
			meta: { width: { ratio: 11, minPx: 80 } },
			header: () => (
				<DefinitionTooltip
					definitionId="openPositionsMargin"
					className="capitalize"
				>
					Margin
				</DefinitionTooltip>
			),
			cell: ({ row }) => (
				<OpenPositionsTableMarginDisplay
					marketIndex={row.original.marketIndex}
				/>
			),
		},
		{
			id: 'liqPrice',
			accessorKey: 'liqPrice',
			meta: { width: { ratio: 11, minPx: 80 } },
			header: () => (
				<DefinitionTooltip
					definitionId="openPositionsLiqPrice"
					className="capitalize"
				>
					Liq. Price
				</DefinitionTooltip>
			),
			cell: ({ row }) => <LiqPriceCell position={row.original} />,
		},
		{
			id: 'tpsl',
			header: 'TP/SL',
			meta: {
				width: { ratio: 18, minPx: 140 },
				bodyCellTestId: 'position-tp-sl',
			},
			cell: ({ row }) => <TpSlCell position={row.original} />,
		},
		{
			id: 'actions',
			meta: {
				width: { fixedPx: 142 },
				bodyCellClassName: 'px-sm',
				headerCellClassName: 'px-sm',
			},
			header: (context) => {
				const { onCloseAllPositions } = getMeta(context);
				return (
					<div className="flex w-full items-center justify-center px-sm">
						<TextButton
							variant="primary"
							size="sm"
							onClick={onCloseAllPositions}
							dataTestId="positions-close-all"
						>
							Close All <Trash size={14} />
						</TextButton>
					</div>
				);
			},
			cell: ({ row }) => <ActionsCell position={row.original} />,
		},
	];
}
